import React, { useState } from 'react';
import { api } from 'common/frontend/api';
import FileUploader from 'components/FileUploader';

import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { useTranslation } from 'services/localization';
import {
	storageRef,
	uploadBytes,
	getDownloadURL,
	deleteObject,
} from 'common/frontend/firebase/storage';
import { FieldValues } from 'common/frontend/firebase/firestore';
import { setOnboardingActionDone } from 'services/onboarding/utils';

interface Props {
	shopId: string;
}

const STORAGE_PATH = 'privacy-policies';

const PrivacyPolicyUpload = (props: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { shopId } = props;
	const [loading, setLoading] = useState<boolean>(false);

	const privacyPolicy = useSelector(ShopSelectors.activeShopPrivacyPolicy);
	const activeShopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const handleUpload = async (file: File, name: string) => {
		setLoading(true);
		try {
			setLoading(true);
			const ref = storageRef();
			const fileRef = storageRef(ref, `${STORAGE_PATH}/${shopId}/${name}`);
			const fileUpload = await uploadBytes(fileRef, file);
			const fileUrl = await getDownloadURL(fileUpload.ref);

			await api()
				.shops.doc(shopId)
				.public.update({
					privacyPolicy: {
						name,
						url: fileUrl,
						verified: false,
					},
				});
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		} finally {
			setOnboardingActionDone('PRIVACY_POLICY', activeShopAnalytics);
			setLoading(false);
		}
	};

	const handleRemove = async () => {
		setLoading(true);
		const fileName = privacyPolicy?.name;

		try {
			if (fileName) {
				const ref = storageRef();
				const fileRef = storageRef(ref, `${STORAGE_PATH}/${shopId}/${fileName}`);
				deleteObject(fileRef);
			}
			await api()
				.shops.doc(shopId)
				.public.update({
					privacyPolicy: FieldValues.delete() as any,
				});
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<FileUploader
			file={privacyPolicy}
			onFileSelected={handleUpload}
			onFileRemoved={handleRemove}
			onError={(err) => {
				dispatch(NotificationActions.showNotification({ message: err, variant: 'error' }));
			}}
			isUploading={loading}
		/>
	);
};

export default PrivacyPolicyUpload;
